import { Loader2 } from "lucide-react";
import { TextField, SelectField, FieldShell } from "@/components/forms";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils";
import { PHONE_CODES, COUNTRIES, CURRENCIES } from "./applicationSteps";
import type { ApplicationFormData } from "./types";
import type { FieldErrors, PersonalInfoField } from "./validation";

interface PersonalInfoStepProps {
  formData: ApplicationFormData;
  onChange: (field: keyof ApplicationFormData, value: string) => void;
  /** Messages to show now, per field (see `visibleErrors`). */
  errors?: FieldErrors<PersonalInfoField>;
  /** Email pulled from the signed-in account; locks the field when present. */
  lockedEmail?: string | null;
  converting: boolean;
  originalSalary: number | null;
  originalCurrency: string;
  onSalaryChange: (value: string) => void;
}

export function PersonalInfoStep({
  formData,
  onChange,
  errors = {},
  lockedEmail,
  converting,
  originalSalary,
  originalCurrency,
  onSalaryChange,
}: PersonalInfoStepProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <TextField
        id="firstName"
        label="First name"
        required
        value={formData.firstName}
        onChange={(v) => onChange("firstName", v)}
        error={errors.firstName}
        placeholder="Jordan"
      />
      <TextField
        id="lastName"
        label="Last name"
        required
        value={formData.lastName}
        onChange={(v) => onChange("lastName", v)}
        error={errors.lastName}
        placeholder="Rivera"
      />

      <TextField
        id="email"
        type="email"
        label="Email"
        required
        value={lockedEmail || formData.email}
        onChange={(v) => onChange("email", v)}
        disabled={!!lockedEmail}
        error={errors.email}
        hint={lockedEmail ? "Pulled from your account" : undefined}
        placeholder="you@email.com"
        containerClassName="sm:col-span-2"
      />

      {/* Phone: country code + number */}
      <FieldShell
        id="phone"
        label="Phone"
        required
        error={errors.phone}
        hint={
          formData.phone
            ? `${formData.phoneCountryCode.replace(/^\+1-(US|CA)$/, "+1")} ${formData.phone}`
            : undefined
        }
      >
        <div className="flex gap-2">
          <Select
            value={formData.phoneCountryCode}
            onValueChange={(v) => onChange("phoneCountryCode", v)}
          >
            <SelectTrigger className="h-11 w-[132px] shrink-0 rounded-xl border bg-[color:var(--landing-surface)] border-[color:var(--landing-glass-border)] text-sm text-[color:var(--landing-text)] focus:ring-2 focus:ring-[var(--acc2)]/40 focus:border-[var(--acc2)]/50 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHONE_CODES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div
            className={cn(
              "auth-input flex h-11 min-w-0 flex-1 items-center rounded-xl px-3.5",
              errors.phone && "!border-destructive/60",
            )}
          >
            <input
              id="phone"
              type="tel"
              inputMode="numeric"
              aria-invalid={errors.phone ? true : undefined}
              value={formData.phone}
              onChange={(e) =>
                onChange("phone", e.target.value.replace(/\D/g, "").slice(0, 15))
              }
              placeholder="Phone number"
              maxLength={15}
              className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-[color:var(--landing-text-dim)]"
              style={{ color: "var(--landing-text)" }}
            />
          </div>
        </div>
      </FieldShell>

      <SelectField
        id="country"
        label="Country"
        value={formData.country}
        onValueChange={(v) => onChange("country", v)}
        options={COUNTRIES}
        placeholder="Select country"
      />

      {/* Expected salary + currency */}
      <FieldShell
        id="expectedSalary"
        label="Expected salary"
        required
        error={errors.expectedSalary}
        hint={
          originalSalary && originalCurrency !== formData.salaryCurrency
            ? `Converted from ${originalCurrency} ${originalSalary.toLocaleString()}`
            : undefined
        }
        className="sm:col-span-2"
      >
        <div className="flex gap-2">
          <div
            className={cn(
              "auth-input flex h-11 min-w-0 flex-1 items-center rounded-xl px-3.5",
              converting && "opacity-60",
              errors.expectedSalary && "!border-destructive/60",
            )}
          >
            <input
              id="expectedSalary"
              type="number"
              aria-invalid={errors.expectedSalary ? true : undefined}
              min="1"
              step="1000"
              value={formData.expectedSalary}
              onChange={(e) => onSalaryChange(e.target.value)}
              placeholder="120000"
              disabled={converting}
              className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-[color:var(--landing-text-dim)]"
              style={{ color: "var(--landing-text)" }}
            />
            {converting && (
              <Loader2 className="h-4 w-4 animate-spin text-[var(--acc2)]" />
            )}
          </div>
          <Select
            value={formData.salaryCurrency}
            onValueChange={(v) => onChange("salaryCurrency", v)}
            disabled={converting}
          >
            <SelectTrigger className="h-11 w-[120px] shrink-0 rounded-xl border bg-[color:var(--landing-surface)] border-[color:var(--landing-glass-border)] text-sm text-[color:var(--landing-text)] focus:ring-2 focus:ring-[var(--acc2)]/40 focus:border-[var(--acc2)]/50 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </FieldShell>
    </div>
  );
}
